import React from 'react';
import { Truck, Users, PackageCheck, PackageX } from 'lucide-react';

/**
 * Compact, scannable summary of the customer's lifting & loading answers.
 * Shown to drivers BEFORE bidding so they can price accordingly.
 *
 * Props:
 *   collectionHelp:   'available' | 'none' | undefined
 *   deliveryHelp:     'available' | 'none' | undefined
 *   driverAssistance: 'driver_only' | 'helper_required' | undefined
 *   compact:          if true, render a single inline pill row (used in cards)
 *   dataTestId
 */
export const LiftingHelpSummary = ({
  collectionHelp,
  deliveryHelp,
  driverAssistance,
  compact = false,
  columns = false,
  stack = false,
  dataTestId,
}) => {
  // If nothing was specified, render nothing — keeps old jobs clean.
  // The three-column card variant always renders so the layout stays stable.
  if (!columns && !collectionHelp && !deliveryHelp && !driverAssistance) return null;

  const helperRequired = driverAssistance === 'helper_required';

  // Three-column Load / Unload / Driver row used on driver job cards.
  if (columns) {
    const col = (Icon, label, value) => (
      <div className="flex items-start gap-2 min-w-0">
        <Icon className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" strokeWidth={2} />
        {stack ? (
          <p className="min-w-0 text-xs leading-tight break-words">
            <span className="font-bold text-gray-900">{label}:</span>{' '}
            <span className="text-gray-500">{value}</span>
          </p>
        ) : (
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 leading-tight">{label}</p>
            <p className="text-xs text-gray-500 leading-tight mt-0.5 break-words">{value}</p>
          </div>
        )}
      </div>
    );
    const helpText = (v) => (v === 'available' ? 'Help available' : v === 'none' ? 'No help' : 'Not specified');
    return (
      <div
        className={`mt-3 pt-3 border-t border-gray-100 grid gap-2 sm:gap-3 ${stack ? 'grid-cols-1' : 'grid-cols-3'}`}
        data-testid={dataTestId}
      >
        {col(PackageCheck, 'Load', helpText(collectionHelp))}
        {col(PackageX, 'Unload', helpText(deliveryHelp))}
        {col(Truck, 'Driver', helperRequired ? 'Helper required' : driverAssistance === 'driver_only' ? 'Driver only' : 'Not specified')}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-1" data-testid={dataTestId}>
        {helperRequired && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-900 text-[11px] font-bold rounded ring-1 ring-amber-200">
            <Users className="w-3 h-3" /> Helper required
          </span>
        )}
        {collectionHelp === 'none' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-800 text-[11px] font-medium rounded ring-1 ring-red-200" title="No-one available to help load at pickup">
            <PackageX className="w-3 h-3" /> No load help
          </span>
        )}
        {deliveryHelp === 'none' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-800 text-[11px] font-medium rounded ring-1 ring-red-200" title="No-one available to help unload at drop-off">
            <PackageX className="w-3 h-3" /> No unload help
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-amber-50/40 border border-amber-200 rounded-md p-3 space-y-2" data-testid={dataTestId}>
      <p className="text-[10px] uppercase tracking-wider text-amber-900 font-bold flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> Lifting &amp; loading
      </p>
      <ul className="text-sm text-gray-800 space-y-1.5">
        {collectionHelp && (
          <li className="flex items-start gap-2" data-testid="help-collection-row">
            {collectionHelp === 'available' ? (
              <PackageCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            ) : (
              <PackageX className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <span className="font-semibold">At pickup: </span>
              {collectionHelp === 'available'
                ? 'Someone available to help load'
                : 'No help available — driver must load alone'}
            </div>
          </li>
        )}
        {deliveryHelp && (
          <li className="flex items-start gap-2" data-testid="help-delivery-row">
            {deliveryHelp === 'available' ? (
              <PackageCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            ) : (
              <PackageX className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <span className="font-semibold">At drop-off: </span>
              {deliveryHelp === 'available'
                ? 'Someone available to help unload'
                : 'No help available — driver must unload alone'}
            </div>
          </li>
        )}
        {driverAssistance && (
          <li className="flex items-start gap-2" data-testid="help-assistance-row">
            {helperRequired ? (
              <Users className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
            ) : (
              <Truck className="w-4 h-4 text-gray-700 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <span className="font-semibold">Driver assistance: </span>
              {helperRequired
                ? 'Driver must bring a helper (factor this into your bid)'
                : 'Driver only — no helper needed'}
            </div>
          </li>
        )}
      </ul>
    </div>
  );
};

export default LiftingHelpSummary;
